import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Image, Video, Search, X, Plus, Edit, Trash, Star, Check, Upload, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import Pagination from '../../components/Pagination';

interface MediaItem {
  id: string;
  title: string;
  description: string;
  storage_path: string;
  media_type: 'image' | 'video';
  is_featured: boolean;
  is_carousel: boolean;
  created_at: string;
  url?: string;
}

export default function GalleryManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMediaItem, setSelectedMediaItem] = useState<MediaItem | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'carousel' | 'featured'>('all');
  const [mediaType, setMediaType] = useState<'all' | 'image' | 'video'>('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    media_type: 'image' as 'image' | 'video',
    is_featured: false,
    is_carousel: false,
    file: null as File | null
  });

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    is_featured: false,
    is_carousel: false
  });

  useEffect(() => {
    fetchMediaItems();
  }, [user]);

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [searchTerm, activeFilter, mediaType]);

  const fetchMediaItems = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery_media')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const itemsWithUrls = await Promise.all(
        (data || []).map(async (item) => {
          const { data: { publicUrl } } = supabase
            .storage
            .from('gallery')
            .getPublicUrl(item.storage_path);
          
          return {
            ...item,
            url: publicUrl
          };
        })
      );

      setMediaItems(itemsWithUrls);
    } catch (error) {
      console.error('Error fetching media items:', error);
      toast.error('Failed to load gallery items');
    } finally {
      setLoading(false);
    }
  };

  const handleMediaUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }

    setLoading(true);
    try {
      const fileExt = uploadForm.file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${uploadForm.media_type}s/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, uploadForm.file);

      if (uploadError) throw uploadError;

      const { data: mediaData, error: dbError } = await supabase
        .from('gallery_media')
        .insert([
          {
            title: uploadForm.title,
            description: uploadForm.description,
            storage_path: filePath,
            media_type: uploadForm.media_type,
            is_featured: uploadForm.is_featured,
            is_carousel: uploadForm.is_carousel
          }
        ])
        .select()
        .single();

      if (dbError) throw dbError;

      toast.success(`${uploadForm.media_type === 'image' ? 'Image' : 'Video'} uploaded successfully`);
      setShowUploadModal(false);
      setUploadForm({
        title: '',
        description: '',
        media_type: 'image',
        is_featured: false,
        is_carousel: false,
        file: null
      });
      fetchMediaItems();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload media');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMediaItem) return;

    try {
      const { error } = await supabase
        .from('gallery_media')
        .update({
          title: editForm.title,
          description: editForm.description,
          is_featured: editForm.is_featured,
          is_carousel: editForm.is_carousel
        })
        .eq('id', selectedMediaItem.id);

      if (error) throw error;

      toast.success('Media item updated successfully');
      setShowEditModal(false);
      setSelectedMediaItem(null);
      fetchMediaItems();
    } catch (error) {
      console.error('Error updating media item:', error);
      toast.error('Failed to update media item');
    }
  };

  const handleDeleteMediaItem = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item? This action cannot be undone.')) return;

    try {
      // Get the storage path first
      const { data: mediaItem, error: fetchError } = await supabase
        .from('gallery_media')
        .select('storage_path')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('gallery')
        .remove([mediaItem.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('gallery_media')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      toast.success('Media item deleted successfully');
      fetchMediaItems();
    } catch (error) {
      console.error('Error deleting media item:', error);
      toast.error('Failed to delete media item');
    }
  };

  const toggleFeatured = async (id: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('gallery_media')
        .update({ is_featured: !currentValue })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Item ${!currentValue ? 'featured' : 'unfeatured'} successfully`);
      fetchMediaItems();
    } catch (error) {
      console.error('Error updating featured status:', error);
      toast.error('Failed to update featured status');
    }
  };

  const toggleCarousel = async (id: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('gallery_media')
        .update({ is_carousel: !currentValue })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Item ${!currentValue ? 'added to' : 'removed from'} carousel successfully`);
      fetchMediaItems();
    } catch (error) {
      console.error('Error updating carousel status:', error);
      toast.error('Failed to update carousel status');
    }
  };

  // Filter media items based on search term and active filter
  const filteredMediaItems = mediaItems.filter(item => {
    const matchesSearch = 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      activeFilter === 'all' || 
      (activeFilter === 'carousel' && item.is_carousel) ||
      (activeFilter === 'featured' && item.is_featured);
      
    const matchesType =
      mediaType === 'all' ||
      mediaType === item.media_type;
    
    return matchesSearch && matchesFilter && matchesType;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredMediaItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredMediaItems.slice(startIndex, endIndex);

  if (loading && !mediaItems.length) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Gallery Management</h1>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Media
          </button>
        </div>

        {/* Filters and Search */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search gallery..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 bg-zinc-900 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              All Items
            </button>
            <button
              onClick={() => setActiveFilter('carousel')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'carousel'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              Carousel
            </button>
            <button
              onClick={() => setActiveFilter('featured')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'featured'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              Featured
            </button>
          </div>

          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => setMediaType('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                mediaType === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              <Filter className="h-4 w-4" />
              All Types
            </button>
            <button
              onClick={() => setMediaType('image')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                mediaType === 'image'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              <Image className="h-4 w-4" />
              Images
            </button>
            <button
              onClick={() => setMediaType('video')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                mediaType === 'video'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              <Video className="h-4 w-4" />
              Videos
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        {currentItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currentItems.map((item) => (
              <div key={item.id} className="bg-zinc-900 rounded-lg overflow-hidden">
                <div className="aspect-square relative">
                  {item.media_type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800 relative">
                      <Video className="h-16 w-16 text-gray-400 absolute" />
                      <video 
                        src={item.url}
                        className="w-full h-full object-cover opacity-50"
                        muted
                        autoPlay
                        loop
                        playsInline
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Play Video
                        </a>
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedMediaItem(item);
                        setEditForm({
                          title: item.title,
                          description: item.description,
                          is_featured: item.is_featured,
                          is_carousel: item.is_carousel
                        });
                        setShowEditModal(true);
                      }}
                      className="p-2 bg-blue-600 rounded-full hover:bg-blue-700"
                      title="Edit"
                    >
                      <Edit className="h-5 w-5 text-white" />
                    </button>
                    <button
                      onClick={() => toggleFeatured(item.id, item.is_featured)}
                      className={`p-2 rounded-full ${
                        item.is_featured 
                          ? 'bg-yellow-600 hover:bg-yellow-700' 
                          : 'bg-zinc-600 hover:bg-zinc-700'
                      }`}
                      title={item.is_featured ? 'Unfeature' : 'Feature'}
                    >
                      <Star className="h-5 w-5 text-white" />
                    </button>
                    <button
                      onClick={() => toggleCarousel(item.id, item.is_carousel)}
                      className={`p-2 rounded-full ${
                        item.is_carousel 
                          ? 'bg-green-600 hover:bg-green-700' 
                          : 'bg-zinc-600 hover:bg-zinc-700'
                      }`}
                      title={item.is_carousel ? 'Remove from Carousel' : 'Add to Carousel'}
                    >
                      <Check className="h-5 w-5 text-white" />
                    </button>
                    <button
                      onClick={() => handleDeleteMediaItem(item.id)}
                      className="p-2 bg-primary-600 rounded-full hover:bg-primary-700"
                      title="Delete"
                    >
                      <Trash className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-white font-semibold truncate">{item.title}</h3>
                  <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      item.media_type === 'image' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {item.media_type === 'image' ? 'Image' : 'Video'}
                    </span>
                    {item.is_featured && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        Featured
                      </span>
                    )}
                    {item.is_carousel && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        Carousel
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-zinc-900 rounded-lg">
            <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 text-xl">No media items found</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 text-primary-500 hover:text-primary-400"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {filteredMediaItems.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredMediaItems.length}
          />
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Upload Media</h2>
                <button onClick={() => setShowUploadModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handleMediaUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-24"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Media Type
                  </label>
                  <select
                    value={uploadForm.media_type}
                    onChange={(e) => setUploadForm({ ...uploadForm, media_type: e.target.value as 'image' | 'video' })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    File
                  </label>
                  <input
                    type="file"
                    accept={uploadForm.media_type === 'image' ? 'image/*' : 'video/*'}
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadForm.is_featured}
                      onChange={(e) => setUploadForm({ ...uploadForm, is_featured: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-300">Featured Item</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadForm.is_carousel}
                      onChange={(e) => setUploadForm({ ...uploadForm, is_carousel: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-300">Add to Carousel</span>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      Upload Media
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedMediaItem && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Edit Media</h2>
                <button onClick={() => setShowEditModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-24"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.is_featured}
                      onChange={(e) => setEditForm({ ...editForm, is_featured: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-300">Featured Item</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.is_carousel}
                      onChange={(e) => setEditForm({ ...editForm, is_carousel: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-300">Add to Carousel</span>
                  </label>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-md font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}